import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { LifeBuoy, Send, X, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";


// KB answers use **bold** markers: render them as <strong>, never as literal asterisks
function renderRich(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
    : <span key={i}>{p.replace(/\*\*/g, "")}</span>));
}

export default function NextmindAssistant() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const openPanel = async () => {
    setOpen(true);
    if (msgs.length === 0) {
      try {
        const { data } = await http.get("/assistant/greeting");
        setMsgs([{ role: "a", text: data.reply }]);
        setSessionId(data.session_id || null);
      } catch { setMsgs([{ role: "a", text: "…" }]); }
    }
  };

  const newChat = async () => {
    setSuggest(false); setInput("");
    try {
      const { data } = await http.post("/assistant/new", {});
      setMsgs([{ role: "a", text: data.reply }]);
      setSessionId(data.session_id || null);
    } catch { setMsgs([]); setSessionId(null); }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "u", text: q }]);
    setBusy(true);
    try {
      const page = window.location.pathname;
      const { data } = await http.post("/assistant/ask", { message: q, page, session_id: sessionId });
      setMsgs((m) => [...m, { role: "a", text: data.reply, action: data.action || null }]);
      setSuggest(!!data.needs_escalation);
      if (data.session_id) setSessionId(data.session_id);
    } catch {
      setMsgs((m) => [...m, { role: "a", text: "Errore temporaneo." }]);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    const context = msgs.map((m) => `${m.role === "u" ? "Utente" : "Assistente"}: ${m.text}`).join("\n");
    const lastQ = [...msgs].reverse().find((m) => m.role === "u")?.text || "Richiesta assistenza";
    try {
      await http.post("/support/tickets", {
        subject: lastQ.slice(0, 80), category: "assistente", priority: "normale",
        message: lastQ, assistant_context: context,
      });
      toast.success("Richiesta creata");
      setOpen(false); setSuggest(false);
      nav("/assistenza");
    } catch { toast.error("Impossibile creare la richiesta"); }
  };

  return (
    <>
      <button
        data-testid="assistant-fab"
        onClick={openPanel}
        className="fixed z-40 bottom-20 lg:bottom-6 right-4 w-14 h-14 rounded-full bg-sky-600 hover:bg-sky-500 text-white shadow-xl flex items-center justify-center"
        aria-label="Assistente NEXTMIND"
      >
        <LifeBuoy className="w-6 h-6" />
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed z-50 bottom-20 lg:bottom-24 right-4 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0F172A] text-white">
            <Sparkles className="w-4 h-4 text-sky-300" />
            <div className="flex-1">
              <div className="font-semibold text-sm">{t("assistant.title")}</div>
              <div className="text-[11px] text-slate-300">{t("assistant.subtitle")}</div>
            </div>
            <button data-testid="assistant-new-chat" onClick={newChat} title={t("assistant.new_chat", "Nuova conversazione")}
              className="p-1 rounded hover:bg-white/10 transition-colors"><RotateCcw className="w-4 h-4" /></button>
            <button data-testid="assistant-close" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${m.role === "u" ? "ml-auto bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                {renderRich(m.text)}
                {m.action?.to && (
                  <button data-testid="assistant-action-btn" onClick={() => { setOpen(false); nav(m.action.to); }}
                    className="mt-2 w-full text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded-lg px-3 py-1.5 hover:bg-sky-100 text-left">
                    → {m.action.label}
                  </button>
                )}
              </div>
            ))}
            {suggest && (
              <button data-testid="assistant-open-ticket" onClick={escalate}
                className="w-full mt-1 text-sm font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded-lg px-3 py-2 hover:bg-sky-100">
                {t("assistant.open_ticket")}
              </button>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t border-slate-200 flex items-center gap-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("assistant.placeholder")}
              className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
            <button data-testid="assistant-send" onClick={send} disabled={busy}
              className="w-9 h-9 rounded-lg bg-sky-600 text-white flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
